import React from "react";
import styles from "./Pagination.module.scss";
import clsx from "clsx";
import { PaginationButton } from "../PaginationButton/PaginationButton";
import { getPaginationItems, SEPARATOR1, SEPARATOR2 } from "./utils";

type PaginationProps = {
  totalPages: number;
  currentPage: number;
  length: number;
  onClick: (page: number) => void;
  /**
   * Optional callback to generate href for each page.
   * When provided, pagination items will render as <a> tags with href,
   * but navigation will still be handled via onClick (preventDefault).
   */
  getHref?: (page: number) => string;
  darkMode?: boolean;
};

export const Pagination: React.FC<
  React.PropsWithChildren<PaginationProps>
> = ({ currentPage, totalPages, length, onClick, getHref, darkMode }) => {
  const pageNumbersToLink = getPaginationItems(currentPage, totalPages, length);

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    page: number
  ) => {
    e.preventDefault();
    onClick(page);
  };

  const renderPageButton = (page: number) => {
    if (getHref) {
      return (
        <a
          href={getHref(page)}
          className={styles.pageButton}
          onClick={(e) => handleClick(e, page)}
          data-content-piece={`page__${page}`}
        >
          {page}
        </a>
      );
    }
    return (
      <button
        className={styles.pageButton}
        onClick={(e) => handleClick(e, page)}
        data-content-piece={`page__${page}`}
      >
        {page}
      </button>
    );
  };

  return (
    <div className={clsx(styles.container, { [styles.dark]: darkMode })} data-content-name="pagination">
      <div key="prev">
        <PaginationButton
          disabled={currentPage == 1}
          onClick={(e) => handleClick(e, currentPage - 1)}
          href={getHref ? getHref(currentPage - 1) : undefined}
          arrow="left"
          dataContentPiece="prev"
        />
      </div>
      <ul className={styles.list}>
        {pageNumbersToLink.map((page) => {
          if (page == SEPARATOR1 || page == SEPARATOR2) {
            return (
              <li key={page} className={clsx(styles.item)}>
                &hellip;
              </li>
            );
          }
          return (
            <li
              key={page}
              className={clsx(styles.item, {
                [styles.active]: currentPage == page,
                [styles.activeSibling]: page == currentPage - 1
              })}
            >
              {renderPageButton(page)}
            </li>
          );
        })}
      </ul>
      <div key="next">
        <PaginationButton
          disabled={currentPage == totalPages}
          onClick={(e) => handleClick(e, currentPage + 1)}
          href={getHref ? getHref(currentPage + 1) : undefined}
          arrow="right"
          dataContentPiece="next"
        />
      </div>
    </div>
  );
};
